import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box } from '@rsos/base-components';
import { ANI_ALI } from '@rsos/components/capstone/Location/constants/dataSources';
import {
  getDataSource,
  isSourceAniAli,
} from '@rsos/components/capstone/Location/helpers';
import {
  hasNotificationPermission,
  pushNotification,
} from '@rsos/components/capstone/PushNotifications/PushNotifications';
import NoResults from '@rsos/components/capstone/Queue/NoResults';
import { EMERGENCY_CALLS, NON_CALLS } from '@rsos/constants/incidentTypes';
import { APPLICATION_AGENT_511 } from '@rsos/constants/locationApplications';
import { APPLE, SXM } from '@rsos/constants/orgsWithQueueText';
import { _911INFORM, _911_INFORM } from '@rsos/constants/spatialLayers';
import { useFlag } from '@rsos/flags';
import useFetchAgencyLinks from '@rsos/hooks/useFetchAgencyLinks';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  setFormPageToShow,
  setSelectedAirAmbulance,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { setPaneOpen } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  removeCall,
  setSelectedCallerID,
  inactiveCalls,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  resetState,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  AIR_AMBULANCE_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import getIsPrimaryChatTab from '@rsos/utils/getIsPrimaryChatTab';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import usePortalType from '@rsos/utils/usePortalType';
import { forceStaticLayerOn } from '../../../actions/spatialLayers';
import { FORM_PAGES } from '../../AirAmbulanceForm/AirAmbulanceForm/constants';
import { transcriptionApi } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import { isExtraInfoOrMeta } from '../../JurisdictionView/helpers';
import AirAmbulance from '../AirAmbulance';
import EmergencyCall from '../EmergencyCall/EmergencyCall';
import { NoResultsWrapper, ScrollableList } from './EmergencyCallsQueue.styles';
import {
  trackClickCall,
  trackClickCallFromQueue,
  trackRemoveCall,
  trackTT911Request,
} from './emergencyCallsQueueTrackings';
import { renderLocationHelper } from './helpers';
import { applyCallFilters } from './utils';

export const NOTIFICATION_TT911_TITLE = 'New Text-to-911 from RapidSOS';

/**
 *
 * @param {object} emergencyCalls - list of 911 emergency calls, but if OutboundSMS is enabled it
 * can also contain TF911 conversation data.
 * [
 *  2223334444:{core:{locations}},
 *  5556667777:{textFrom911:{data}}
 * ]
 * @param {boolean} hasAgent511Access - does user have access to view Agent511 text-to-911 requests?
 * @param {String} hoveredCall - ID of call being hovered on the map or in the queue
 * @param {boolean} isVisible - is the current queue visible?
 * or caller ID for use with the Google Maps
 * @param {string} selectedCallerID - the selected caller id
 * @param {Function} onQueueHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {Function} onQueueItemClicked- callback when a queue item is clicked
 * @param {Function} onViewRcsEvent - function that the rcs toast action button uses to open relevant event
 */

const EmergencyCallsQueue = ({
  emergencyCalls = {},
  hasAgent511Access = false,
  hoveredCall = '',
  isVisible = false,
  selectedCallerID = '',
  irpVersion = null,
  onQueueHover = () => {},
  onRemoveID = () => {},
  onQueueItemClicked = () => {},
  onViewRcsEvent = () => {},
}) => {
  const dispatch = useDispatch();
  const psaps = useSelector(state => state.psaps);

  const sinatra = useSelector(state => state.sinatra);

  const hasAniAliAccess = checkAniAliAccessSelector({ psaps, sinatra });

  const hasCallStatusAccess = checkCallStatusAccessSelector({ psaps });

  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  const {
    selectedAirAmbulance = null,
    requests: allAirAmbulances = { nonCalls: {}, calls: {} },
  } = useSelector(state => state.airAmbulance || {});

  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();
  const hasAirAmbulanceEtlIntegrations = useFlag(
    'air_ambulance_etl_integrations',
  );
  const hasGenericResourceRequests = useFlag('generic_resource_requests');
  const showRecentTranscripts = useFlag('show_recent_transcripts');

  const { callFilters, hasActiveCallFilters } = useCallQueueFilters();

  const application = sinatra.user.currentRole?.application;
  const workStation =
    sinatra.user.profile?.attributes?.[application]?.call_taker_mode_settings
      ?.workstation_position || null;

  /**
   * @constant {hoveredCall} - queue/map primary location
   * @constant {hoveredCallerID} - queue/map secondary location (deckGL)
   */
  const hoveredCallerID = useSelector(
    state => state?.hoveredIncident?.hoveredCallerID,
  );

  // If Agent511 is enabled, filter the emergency calls into 2 lists:
  // Text-to-911 requests and 911 calls. Text-to-911 requests will persist on top of the queue,
  // and 911 calls will display below text-to-911 requests.
  const tt911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.keys(emergencyCalls).filter(callerID => {
          const call = emergencyCalls[callerID];
          return (
            call?.isTT911 ||
            call.core?.locations[0]?.application === APPLICATION_AGENT_511
          );
        })
      : [];
  }, [emergencyCalls, hasAgent511Access]);

  const nonTT911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.keys(emergencyCalls).filter(callerID => {
          const call = emergencyCalls[callerID];
          return (
            !call?.isTT911 &&
            call.core?.locations[0]?.application !== APPLICATION_AGENT_511
          );
        })
      : [];
  }, [emergencyCalls, hasAgent511Access]);
  const [rightClickedCallerID, setRightClickedCallerID] = useState(null);
  const [rightClickedAirAmbulanceID, setRightClickedAirAmbulanceID] =
    useState(null);
  const [isRemoveCallVisible, setIsRemoveCallVisible] = useState(false);
  const [isRemoveAirAmbulanceVisible, setIsRemoveAirAmbulanceVisible] =
    useState(false);
  const [callsClickedList, setCallsClickedList] = useState([]);
  const [newTT911RequestID, setNewTT911RequestID] = useState('');
  const { portalType } = usePortalType();
  const transcriptionQuerySubscriptionsRef = useRef(new Map());

  const isTranscriptionApiMiddlewareRegistered = useSelector(
    state => state?.transcriptionApi?.config?.middlewareRegistered === true,
  );

  const handleSelected = id => {
    const mostRecentLocation = getMostRecentLoc(emergencyCalls[id]);

    if (getIsPrimaryChatTab(mostRecentLocation)) {
      dispatch(setSelectedTab(CHAT_TAB));
    } else {
      dispatch(resetState());
    }

    if (!emergencyCalls[id]?.core?.locations) {
      return dispatch(setSelectedCallerID(id));
    }

    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
    const isConnectedCar = isCallerIDFromOrg(SXM, emergencyCalls, id);

    const isAniAli = isSourceAniAli(mostRecentLocation);
    const serviceType = isAniAli ? ANI_ALI : getDataSource(mostRecentLocation);
    const classOfServices = isAniAli
      ? mostRecentLocation?.class_of_service_category || null
      : null;

    const callStatus = getMostRecentCallStatus({
      callerID: id,
      callQueue: emergencyCalls,
      hasAniAliAccess,
      hasCallStatusAccess,
    });

    if (!callsClickedList.includes(id)) {
      trackClickCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        serviceType,
        classOfServices,
        callStatus,
      );
      setCallsClickedList(callsClickedList.concat(id));
    }

    trackClickCallFromQueue(
      isConnectedCar,
      irpVersion,
      payloadID,
      requestID,
      portalType,
      serviceType,
      classOfServices,
      callStatus,
    );
    dispatch(setSelectedCallerID(id));

    const has911informData =
      !!emergencyCalls?.[id]?.additionalData?.[_911_INFORM];

    if (has911informData) {
      dispatch(forceStaticLayerOn(_911INFORM));
    }
  };

  const handleRightClickCallerID = (e, callerID) => {
    e.preventDefault();
    e.stopPropagation();
    setRightClickedAirAmbulanceID(null);
    if (selectedCallerID !== callerID) setRightClickedCallerID(callerID);
  };
  const handleRightClickAirAmbulance = (e, airAmbulanceID) => {
    e.preventDefault();
    e.stopPropagation();
    setRightClickedCallerID(null);
    if (selectedAirAmbulance?.airAmbulanceID !== airAmbulanceID)
      setRightClickedAirAmbulanceID(airAmbulanceID);
  };

  const handleRemoveCall = id => {
    const hasCoreLocations = emergencyCalls[id.caller_id]?.core?.locations;
    if (hasCoreLocations) {
      const mostRecentLocation = getMostRecentLoc(emergencyCalls[id.caller_id]);
      const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);

      const isConnectedCar = isCallerIDFromOrg(SXM, emergencyCalls, id);

      const callStatus = getMostRecentCallStatus({
        callerID: id.caller_id,
        callQueue: emergencyCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      trackRemoveCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        callStatus,
      );
    }
    dispatch(inactiveCalls(id));
    onRemoveID(id.caller_id);
    dispatch(removeCall(id.caller_id));
    setIsRemoveCallVisible(false);
    dispatch(setHoveredID(null));
    toast.dismiss(`toastRcsAvailable-${id.caller_id}`);
  };

  const onClickHandler = useCallback(() => {
    setRightClickedCallerID(null);
    setRightClickedAirAmbulanceID(null);
  }, []);

  window.oncontextmenu = () =>
    window.removeEventListener('click', onClickHandler, false);

  useEffect(() => {
    window.addEventListener('click', onClickHandler, false);

    return () => {
      window.removeEventListener('click', onClickHandler, false);
    };
  }, [onClickHandler]);

  const handleAirAmbulanceClicked = async id => {
    // need to wait for dispatch(viewAllCalls) to finish running before setting selected Air Ambulance
    if (selectedCallerID) {
      await dispatch(viewAllCalls());
    }
    dispatch(setSelectedAirAmbulance(id));
    dispatch(setFormPageToShow(FORM_PAGES.STATUS));
    dispatch(closeCard(AIR_AMBULANCE_CARD));
    dispatch(setPaneOpen(true));
  };

  const shouldDisplayCall = useCallback(
    item => {
      if (!hasActiveCallFilters) return true;
      return applyCallFilters(item, callFilters, workStation);
    },
    [callFilters, hasActiveCallFilters, workStation],
  );

  const hasAdminCallIndicatorEnabled = useFlag('admin_call_indicator');

  useEffect(() => {
    if (!showRecentTranscripts || !isTranscriptionApiMiddlewareRegistered) {
      transcriptionQuerySubscriptionsRef.current.forEach(subscription => {
        subscription?.unsubscribe?.();
      });
      transcriptionQuerySubscriptionsRef.current.clear();
      return;
    }

    const queueCallerIds = Object.keys(emergencyCalls || {});
    const queueCallerSet = new Set(queueCallerIds);

    queueCallerIds.forEach(callerId => {
      if (transcriptionQuerySubscriptionsRef.current.has(callerId)) return;

      const location0 = emergencyCalls?.[callerId]?.core?.locations?.[0];
      const conversationStart = location0?.conversation_start_time;
      const callStart = location0?.call_start_time;
      const callStartTime =
        typeof conversationStart === 'number'
          ? conversationStart
          : typeof callStart === 'number'
            ? callStart
            : null;

      const subscription = dispatch(
        transcriptionApi.endpoints.fetchTranscriptions.initiate({
          callerId,
          callStartTime,
        }),
      );
      transcriptionQuerySubscriptionsRef.current.set(callerId, subscription);
    });

    transcriptionQuerySubscriptionsRef.current.forEach(
      (subscription, callerId) => {
        if (queueCallerSet.has(callerId)) return;
        subscription?.unsubscribe?.();
        transcriptionQuerySubscriptionsRef.current.delete(callerId);
      },
    );
  }, [
    dispatch,
    emergencyCalls,
    isTranscriptionApiMiddlewareRegistered,
    showRecentTranscripts,
  ]);

  useEffect(() => {
    const subscriptions = transcriptionQuerySubscriptionsRef.current;
    return () => {
      subscriptions.forEach(subscription => {
        subscription?.unsubscribe?.();
      });
      subscriptions.clear();
    };
  }, []);

  /**
   * @function renderQueueItem - reorder the callQueue based on the latest location or secondaryLocation
   */
  const renderQueueItem = queue =>
    queue.map(callerID => {
      const item = emergencyCalls[callerID];
      const {
        mostRecentLocRecord,
        isManualSearch,
        isCallerIn911informBuilding,
        isCallerInConnectedSitesBuilding,
        hasGoogleCarCrashData,
        hasGoogleCallerInfo,
        isTT911,
        isAdmin,
        source,
        conversation_id,
      } = renderLocationHelper(item, emergencyCalls, hasAgent511Access);

      const callStatus = getMostRecentCallStatus({
        callerID,
        callQueue: emergencyCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      //TODO vkoo: investigate why this condition is necessary. PR #21382
      if (!callerID) {
        return null;
      }

      return (
        <Box
          callerid={callerID}
          key={`${callerID}`}
          noShrink={true}
          onContextMenu={e => {
            handleRightClickCallerID(e, callerID);
          }}
          onMouseEnter={() => onQueueHover(callerID, 'queue', EMERGENCY_CALLS)}
          onMouseLeave={() => onQueueHover(null, 'queue', EMERGENCY_CALLS)}
          onClick={() => onQueueItemClicked(callerID)}
          data-name={`${source}-${callerID}`}
          display={shouldDisplayCall(item) ? 'flex' : 'none'}
        >
          <EmergencyCall
            applicationType={mostRecentLocRecord.application}
            classOfService={mostRecentLocRecord?.class_of_service_category}
            callerID={callerID}
            callStatus={callStatus}
            rightClickedCallerID={rightClickedCallerID}
            hasAgent511Access={hasAgent511Access}
            hasGoogleCarCrashData={hasGoogleCarCrashData}
            hasGoogleCallerInfo={hasGoogleCallerInfo}
            isApple={isCallerIDFromOrg(APPLE, emergencyCalls, callerID)}
            isCallerIn911informBuilding={isCallerIn911informBuilding}
            isCallerInConnectedSitesBuilding={isCallerInConnectedSitesBuilding}
            isCallReceived={item?.agencyShareChat?.isCallReceived}
            isCallShared={item?.agencyShareChat?.isCallShared}
            isConnectedCar={isCallerIDFromOrg(SXM, emergencyCalls, callerID)}
            isManualSearch={isManualSearch}
            isMapMarkerHovered={
              callerID === hoveredCall || callerID === hoveredCallerID
            }
            isRemoveCallVisible={isRemoveCallVisible}
            isSelected={callerID === selectedCallerID}
            isTT911={isTT911}
            isAdmin={isAdmin && hasAdminCallIndicatorEnabled}
            isUnread={
              item?.showNewCallNotification || item?.agencyShareChat?.isUnread
            }
            ui={item?.ui}
            isUnseen={item?.agencyShareChat?.isUnseen}
            onRemoveCall={handleRemoveCall}
            setCallerID={handleSelected}
            source={source}
            tt911ADR={item?.additionalData?.[APPLICATION_AGENT_511]?.[0]}
            conversationID={conversation_id}
            onViewRcsEvent={onViewRcsEvent}
          />
        </Box>
      );
    });

  const renderAirAmbulancesQueueItem = (queue, isInactive) => {
    return Object.keys(queue).map(airAmbulanceID => {
      return (
        <Box
          callerid={airAmbulanceID}
          key={`${airAmbulanceID}`}
          noShrink={true}
          onContextMenu={e => handleRightClickAirAmbulance(e, airAmbulanceID)}
          onMouseEnter={() => onQueueHover(airAmbulanceID, 'queue', NON_CALLS)}
          onMouseLeave={() => onQueueHover(null, 'queue', NON_CALLS)}
          onClick={() => handleAirAmbulanceClicked(airAmbulanceID)}
          data-name={`air-ambulance-${airAmbulanceID}`}
        >
          <AirAmbulance
            applicationType={'gmr'}
            airAmbulanceID={airAmbulanceID}
            isSelected={airAmbulanceID === selectedAirAmbulance?.airAmbulanceID}
            isInactive={isInactive}
            startTime={queue[airAmbulanceID].created_time}
            rightClickedAirAmbulanceID={rightClickedAirAmbulanceID}
            isRemoveAirAmbulanceVisible={isRemoveAirAmbulanceVisible}
            setIsRemoveAirAmbulanceVisible={setIsRemoveAirAmbulanceVisible}
            status={queue[airAmbulanceID]?.body?.status}
          />
        </Box>
      );
    });
  };

  useEffect(() => {
    if (
      tt911Requests &&
      tt911Requests.length &&
      numberWithNewLocation &&
      hasNotificationPermission(irpVersion)
    ) {
      const tt911Request = tt911Requests.find(
        tt911ID =>
          emergencyCalls[tt911ID]?.core?.locations?.length === 1 &&
          emergencyCalls[tt911ID]?.core?.locations?.[0].caller_id ===
            numberWithNewLocation,
      );
      if (tt911Request) {
        setNewTT911RequestID(tt911Request);
      }
    }
  }, [tt911Requests, numberWithNewLocation, irpVersion, emergencyCalls]);

  useEffect(() => {
    if (newTT911RequestID) {
      pushNotification({
        title: NOTIFICATION_TT911_TITLE,
      });
      setNewTT911RequestID('');
    }
  }, [newTT911RequestID]);

  useEffect(() => {
    if (newTT911RequestID && emergencyCalls[newTT911RequestID]) {
      const callLocations = emergencyCalls[newTT911RequestID].core.locations;
      const firstLocationIndex = callLocations?.length - 1 || 0;
      const { payloadID, requestID } = isExtraInfoOrMeta(
        callLocations[firstLocationIndex],
      );
      trackTT911Request(irpVersion, payloadID, requestID);
    }
  }, [emergencyCalls, irpVersion, newTT911RequestID]);

  // Check agency share/chat access and fetch paired peer agencies.
  useFetchAgencyLinks();

  return (
    isVisible && (
      <>
        {Object.keys(emergencyCalls).length ||
        Object.keys(allAirAmbulances?.nonCalls || {}).length ? (
          <ScrollableList>
            {
              // If etl_integrations_based Air ambulance is enabled and the resource request panel is enabled, then we'll put them in the panel instead of the queue
              !(hasAirAmbulanceEtlIntegrations && hasGenericResourceRequests) &&
              hasAirAmbulanceAccess &&
              Object.keys(allAirAmbulances?.nonCalls || {}).length ? (
                <>
                  <Box data-name="air-ambulances">
                    {renderAirAmbulancesQueueItem(allAirAmbulances.nonCalls)}
                  </Box>
                </>
              ) : null
            }
            {hasAgent511Access ? (
              <>
                <Box data-name="tt911-requests">
                  {renderQueueItem(tt911Requests)}
                </Box>
                <Box data-name="non-tt911-requests">
                  {renderQueueItem(nonTT911Requests)}
                </Box>
              </>
            ) : (
              <Box data-name="emergency-calls">
                {renderQueueItem(Object.keys(emergencyCalls))}
              </Box>
            )}
          </ScrollableList>
        ) : (
          <NoResultsWrapper>
            <NoResults
              title="No Emergencies"
              description="No emergency events were found."
            />
          </NoResultsWrapper>
        )}
      </>
    )
  );
};

export default EmergencyCallsQueue;
